"use client";

import { createContext, useContext, useEffect, useState } from "react";
import useUiThemeState from "~/stores/ui/theme";

export type Mode = "dark" | "light" | "system";
export type Theme =
  | "zinc"
  | "zinc"
  | "rose"
  | "blue"
  | "green"
  | "orange"
  | "red"
  | "yellow"
  | "violet";

export type ThemeProviderProps = {
  children: React.ReactNode;
  defaultMode?: Mode;
  defaultTheme?: Theme;
  storageModeKey?: string;
  storageThemeKey?: string;
};

const initialState: ThemeProviderState = {
  mode: "dark",
  theme: "zinc",
  setTheme: () => null,
  setMode: () => null,
};

export function ThemeProvider({
  children,
  defaultMode = "dark",
  defaultTheme = "zinc",
  storageModeKey = "so-ui-mode",
  storageThemeKey = "so-ui-theme",
  ...props
}: ThemeProviderProps) {
  const themeState = useUiThemeState();
  const [theme, setTheme] = useState<Theme>(themeState.theme ?? defaultTheme);
  const [mode, setMode] = useState<Mode>(themeState.mode ?? defaultMode);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      themeState.setTheme(theme);
      setTheme(theme);
    },
    mode,
    setMode: (mode: Mode) => {
      themeState.setMode(mode);
      setMode(mode);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export type ThemeProviderState = {
  mode: Mode;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  setMode: (mode: Mode) => void;
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
